import { useMemo, useRef } from "react";
import { useFrame } from "@react-three/fiber";
import * as THREE from "three";
import { XR_PALETTE } from "./xrAssets";

const haloTex = (() => {
  let t;
  return () => {
    if (t) return t;
    const c = document.createElement("canvas"); c.width = c.height = 128;
    const g = c.getContext("2d"), gr = g.createRadialGradient(64, 64, 0, 64, 64, 64);
    gr.addColorStop(0, "rgba(255,226,154,0.95)"); gr.addColorStop(0.35, "rgba(224,179,58,0.45)"); gr.addColorStop(1, "rgba(224,179,58,0)");
    g.fillStyle = gr; g.fillRect(0, 0, 128, 128);
    return (t = new THREE.CanvasTexture(c));
  };
})();

/** Graine de Vie: emissive golden PBR seed with an additive halo, gentle bob and pulse; clickable (mouse / XR ray) for preview. */
export const Seed = ({ position, side, onPick, testId }) => {
  const g = useRef(), halo = useRef(), tex = useMemo(haloTex, []);
  useFrame(({ clock }) => {
    const t = clock.elapsedTime;
    if (g.current) { g.current.rotation.y = t * 1.2; g.current.position.y = Math.sin(t * 2) * 0.012; }
    if (halo.current) halo.current.scale.setScalar(0.2 + Math.sin(t * 3) * 0.02);
  });
  return (
    <group position={position} name={testId} userData={{ side }}>
      <group ref={g} onClick={(e) => { e.stopPropagation(); onPick?.(); }}>
        <mesh scale={[0.042, 0.058, 0.042]}><icosahedronGeometry args={[1, 3]} />
          <meshStandardMaterial color={XR_PALETTE.gold} emissive="#C98F12" emissiveIntensity={1.1} metalness={0.45} roughness={0.3} /></mesh>
        <mesh position-y={0.062} rotation-z={0.5} scale={[0.02, 0.008, 0.012]}><sphereGeometry args={[1, 8, 6]} /><meshStandardMaterial color={XR_PALETTE.teal} emissive={XR_PALETTE.teal} emissiveIntensity={0.8} roughness={0.5} /></mesh>
      </group>
      <sprite ref={halo} scale={0.2}><spriteMaterial map={tex} opacity={0.65} blending={THREE.AdditiveBlending} depthWrite={false} transparent toneMapped={false} /></sprite>
      <mesh rotation-x={Math.PI / 2}><torusGeometry args={[0.085, 0.004, 6, 40]} /><meshBasicMaterial color={XR_PALETTE.teal} transparent opacity={0.75} toneMapped={false} /></mesh>
    </group>
  );
};

/** Burst of golden sparks when a seed is collected (one instanced draw call, 0.8 s). */
export const Burst = ({ at, born }) => {
  const im = useRef(), dirs = useMemo(() => Array.from({ length: 24 }, () => new THREE.Vector3(Math.random() - 0.5, Math.random() * 0.8, Math.random() - 0.5).normalize()), []);
  const m = useMemo(() => new THREE.Matrix4(), []);
  useFrame(() => {
    if (!im.current) return;
    const k = (performance.now() - born) / 800;
    im.current.visible = k < 1;
    dirs.forEach((d, i) => { m.makeScale(0.012 * (1 - k), 0.012 * (1 - k), 0.012 * (1 - k)).setPosition(d.clone().multiplyScalar(0.25 * k).add(at)); im.current.setMatrixAt(i, m); });
    im.current.instanceMatrix.needsUpdate = true;
  });
  return <instancedMesh ref={im} args={[null, null, dirs.length]}><sphereGeometry args={[1, 6, 4]} /><meshBasicMaterial color="#FFE29A" toneMapped={false} /></instancedMesh>;
};
